/**
 * CONTROL GEOMETRY: the sizes, radii, type scale and stacking order
 * every stratum builds from. Mode-independent, so plain constants.
 * The radius scale is the one the app already used: chips and buttons
 * 4, tooltips and fields 6, the glass panel 16. The type scale is the
 * observed one: 11 micro, 12 caption, 13 control, 14 body.
 */

export const FONT = {
  sans: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif",
  mono: 'ui-monospace, SFMono-Regular, Menlo, monospace',
} as const;

export const TEXT = {
  /** The mark's label above a leaf's box on the printed page: one step under micro. */
  nano: 10,
  micro: 11,
  caption: 12,
  control: 13,
  body: 14,
  status: 14,
  /** Title level 4 (Title's h4 -- one step under title/h3). */
  subtitle: 16,
  title: 20,
  /** Title level 2 (Title's h2 -- one step under display/h1). */
  /** A screen's own title (Figma 3 sets Questions at 22). */
  screen: 22,
  headline: 24,
  display: 30,
} as const;

export const CONTROL = {
  height: 32,
  heightSm: 24,
  padX: 12,
  padXSm: 8,
  /** Chip's horizontal padding: tighter than padXSm. */
  padXChip: 7,
  /** Tooltip bubble and listbox item padding (both compact popovers). */
  padYCompact: 6,
  padXCompact: 10,
  /** Grid header row's vertical padding. */
  padYHeader: 8,
  /** Grid data cell's vertical padding, one px looser than the header. */
  padYCell: 9,
  /** The block button (a row's action column): the primary and the alternative, the quiet retraction, and the pad above and below the label. */
  block: 44,
  blockGhost: 36,
  padYBlock: 9,
  /** The answer box's height. */
  area: 64,
  /** A row card's side padding (its top and bottom are SPACE.lg), and a note's top and bottom. */
  padYRow: 18,
  padYNote: 14,
  /** The status dot's diameter (StatusDot). */
  dot: 8,
  /** The gap between a button's label and its keyboard letter (Figma 00). */
  gapKey: 6,
  /** A popover list's least width (a pill's list is wider than its trigger). */
  listMin: 160,
} as const;

export const RADIUS = {
  /** The mark on a mark: a swatch's and a highlighted word's corner. */
  hair: 2,
  /** A leaf label on the page and the corner of a held entry's boxes, and the progress track's. */
  xs: 3,
  sm: 4,
  md: 6,
  /** The block buttons: the Review cockpit's and a row's action column (Figma: 8). */
  button: 8,
  /** A category tile, a row card, the note that closes a list, and the done screen's flat card (Figma: 10). */
  card: 10,
  /** The book band (Figma: 12). */
  band: 12,
  lg: 16,
  /** A pill: any radius past half the height; the badge and the count. */
  pill: 999,
} as const;

/** The Review cockpit's page (Figma 00a): the column's width, the tile's width and the gap between tiles, and the room the fixed mode toggle takes at the header's end. */
export const PAGE_COLUMN = { width: 1080, tile: 326, tileGap: 14, padX: 24, padY: 36, logo: 22, toggleRoom: 44, gap: 22 } as const;

export const SPACE = {
  /** The tightest gap: a title's own inner stack (Notice). */
  xxs: 2,
  xs: 4,
  /** A reader's line above and below its words. */
  nudge: 5,
  /** Between xs and sm: the icon-label gap on a button, a rollup's tags. */
  tight: 6,
  sm: 8,
  /** Between sm and md: the gap of a bar's label and its note, a tile's inner stack, a note's top and bottom. */
  snug: 10,
  md: 12,
  /** Between md and lg: a column head's top, a rail band's side. */
  cozy: 14,
  lg: 16,
  /** Between lg and xl: the side padding of the cockpit's bars and rails. */
  wide: 20,
  xl: 24,
  xxl: 32,
} as const;

/** A border's width: the hairline every box wears, and the heavier line of the one in progress. */
export const BORDER = { line: 1, active: 2 } as const;

/** What a box in progress gives up of its padding so the heavier border does not move its contents. */
export const ACTIVE_SHRINK = BORDER.active - BORDER.line;

/** A button's padding by size (the sizes that are not a fixed height): above and below, and beside the label. */
export const BUTTON = { padYCompact: 6, padXCompact: 10, padYBar: 11, padXBar: 14 } as const;

/** A chip's padding by size: a row's badge, a bar's pill, a choice (a reason), and the height of a stratum's paint chip. */
export const CHIP = {
  row: { padY: 2, padX: 8 },
  bar: { padY: 3, padX: 10 },
  choice: { padY: 7, padX: 11 },
  paintHeight: 20,
} as const;

export const Z = {
  overlay: 10,
  popover: 100,
  modal: 500,
  toggle: 1000,
} as const;

/** The uppercase tracked caption every panel heading wears. */
export const TRACKING = {
  label: '0.05em',
  title: '-0.025em',
} as const;

/** Chrome widths the shell lays out by. */
export const FRAME = {
  sidebar: 340,
  card: 380,
  overlayInset: 24,
  /** A floating popover's max extent: the tooltip bubble's width, the
   * select's listbox height. */
  popoverMax: 320,
  /** Vertical allowance for an OverlayColumn's own chrome (its header
   * strip and margins) beyond its edge inset, when bounding its height. */
  overlayColumnChrome: 96,
} as const;

/** The printed page's drawing: the step one press of Zoom takes, a mark's wash, and its border width. */
export const PAGE = {
  zoomStep: 1.5,
  markWash: 0.18,
  markBorder: 2,
  /** A leaf's box wash: faint, so the print reads through (Figma 00: 5-6%), a little more on the leaf being read (8%). */
  leafWash: 0.06,
  leafWashBeing: 0.08,
  /** How far the settled dashed box sits inside the box it settled (Figma 00). */
  settledInsetX: 4,
  settledInsetY: 6,
} as const;

/**
 * The kind walk's frame (Figma 00, 22:10): the leaves column's width, the sheet's width, the new-box popover's
 * width and where it sits under its box (a fraction of the sheet), a mark's label and its reshape handles, the
 * readers strip's chip column, and the progress bar's track.
 */
export const WALK = {
  side: 440,
  sheet: 540,
  popover: 240,
  popoverLeft: 0.0626,
  popoverGap: 12,
  /** Past this fraction down the sheet a drawn box's card opens above the box. */
  popoverFlip: 0.55,
  /** The most the settled print takes in the card, and the most the drafted fixes take of the leaves column. */
  printMax: 120,
  draftsMax: 213,
  labelHeight: 14,
  labelGap: 3,
  /** The padding of a mark's label, and the side padding of a held box's note. */
  labelPadY: 1,
  labelPadX: 5,
  noteLabelPadX: 6,
  handle: 8,
  trackWidth: 160,
  trackHeight: 6,
  dot: 10,
  editRows: 96,
  words: 42,
  wordWidth: 74,
} as const;

/**
 * The article-done frame (Figma 5, 5:129): the centred column's widest, a tally bar's label and count columns
 * and its track's height, the figure a tally shows large, and a flat card's padding.
 */
export const ARTICLE = {
  sheet: 932,
  barLabel: 110,
  barCount: 36,
  barHeight: 8,
  tally: 28,
  cardPadY: 16,
  cardPadX: 18,
} as const;

/** The held screen's frame (Figma 4, 6:2): the entries column's width and the page's width on the canvas. */
export const HELD = { list: 380, sheet: 540 } as const;

/**
 * The rule screen's frame (Figma 2:2, 4:2): the rail's width, the printed page's card, the least width of the
 * tray's label column, and the line the fence's note box takes.
 */
export const RULE = {
  rail: 520,
  sheet: 720,
  label: 72,
  /** The block buttons' least height (Figma: 44) and the Back button's width. */
  block: 44,
  back: 120,
} as const;
